/**
 * Offcanvas — minimal slide-in panel
 *
 * Aktiviert über delegated events:
 *   <button data-bs-toggle="offcanvas" data-bs-target="#id">
 *   <button data-bs-dismiss="offcanvas">
 *
 * Verhalten:
 *  - Slide-in via .show Klasse (CSS animiert)
 *  - Body-Scroll-Lock
 *  - Click auf Backdrop schließt
 *  - ESC-Taste schließt
 *  - Focus-Trap im offenen Panel
 *  - prefers-reduced-motion: CSS-Transition wird durch globalen Guard reduziert
 */

const PANEL_SELECTOR = ".offcanvas";
const BACKDROP_CLASS = "offcanvas-backdrop";

let activePanel = null;
let activeTrigger = null;
let backdrop = null;

const focusableSelector =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function ensureBackdrop() {
  if (backdrop) return backdrop;
  backdrop = document.createElement("div");
  backdrop.className = BACKDROP_CLASS;
  backdrop.addEventListener("click", () => close());
  return backdrop;
}

function open(panel, trigger) {
  if (activePanel) close();
  activePanel = panel;
  activeTrigger = trigger;

  document.body.appendChild(ensureBackdrop());
  // double rAF gives CSS time to register the initial state before transition
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      backdrop.classList.add("show");
      panel.classList.add("show");
    });
  });
  panel.removeAttribute("inert");
  panel.setAttribute("aria-hidden", "false");
  document.body.style.overflow = "hidden";
  // offcanvas-open → CSS blendet Header-Controls (Burger-X + Spenden-CTA) aus,
  // solange das Panel offen ist (verhindert Doppel-X auf Tablet).
  document.body.classList.add("offcanvas-open");
  // aria-expanded → CSS-Icon-Morph Hamburger → X (Button bleibt im Header-Flow)
  if (trigger) trigger.setAttribute("aria-expanded", "true");

  // Move focus into panel
  const firstFocusable = panel.querySelector(focusableSelector);
  if (firstFocusable) firstFocusable.focus();
}

function close() {
  if (!activePanel) return;
  activePanel.classList.remove("show");
  activePanel.setAttribute("aria-hidden", "true");
  activePanel.setAttribute("inert", "");
  if (backdrop) backdrop.classList.remove("show");
  document.body.style.overflow = "";
  document.body.classList.remove("offcanvas-open");
  // aria-expanded zurücksetzen → CSS-Icon-Morph X → Hamburger
  if (activeTrigger) activeTrigger.setAttribute("aria-expanded", "false");

  const trigger = activeTrigger;
  const closingPanel = activePanel;
  activePanel = null;
  activeTrigger = null;

  // Remove backdrop after transition
  setTimeout(() => {
    if (backdrop && !activePanel && backdrop.parentNode) {
      backdrop.parentNode.removeChild(backdrop);
    }
  }, 350);

  if (trigger && typeof trigger.focus === "function") trigger.focus();
  closingPanel.dispatchEvent(new CustomEvent("offcanvas:hidden"));
}

function handleClick(event) {
  const toggle = event.target.closest('[data-bs-toggle="offcanvas"]');
  if (toggle) {
    event.preventDefault();
    const targetSelector = toggle.getAttribute("data-bs-target");
    if (!targetSelector) return;
    const target = document.querySelector(targetSelector);
    if (!target) return;
    // Fix #2: erneuter Klick auf denselben Burger → schließen (Toggle-Verhalten)
    if (target === activePanel) {
      close();
      return;
    }
    open(target, toggle);
    return;
  }

  const dismiss = event.target.closest('[data-bs-dismiss="offcanvas"]');
  if (dismiss) {
    event.preventDefault();
    close();
  }
}

function handleKeydown(event) {
  if (event.key !== "Escape" || !activePanel) return;
  close();
}

function handleFocusTrap(event) {
  if (event.key !== "Tab" || !activePanel) return;
  const focusables = activePanel.querySelectorAll(focusableSelector);
  if (focusables.length === 0) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function init() {
  // Mark all offcanvas panels closed initially.
  // Move panels to <body> so they escape any parent stacking context
  // (e.g. .ds-header is position:sticky with z-index:50 — would trap offcanvas below backdrop).
  document.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
    panel.setAttribute("aria-hidden", "true");
    panel.setAttribute("inert", "");
    if (panel.parentNode !== document.body) {
      document.body.appendChild(panel);
    }
  });

  document.addEventListener("click", handleClick);
  document.addEventListener("keydown", handleKeydown);
  document.addEventListener("keydown", handleFocusTrap);
}
